import { useState, useEffect } from 'react';
import { Storage } from '../storage';
import { TIMELINE_CATEGORIES } from '../constants';
import { timeAgo, formatDate, hasTextContent, stripHtml, getTimelinePreviewImage } from '../utils';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import NotificationBell from '../components/NotificationBell';
import ReportModal from '../components/ReportModal';
import AddToCollectionModal from '../components/AddToCollectionModal';

// ─── PUBLIC PROFILE PAGE ──────────────────────────────────────────────────────
//
//  Shows a user's public profile: avatar, bio, follower/following counts,
//  follow/unfollow button, and a grid of their public timelines.
//
//  Props:
//    userId              {string}    ID of the profile to view
//    currentUser         {object}    the signed-in user
//    onBack              {function}  navigate back
//    onViewTimeline      {function}  (tl, author) => void
//    onViewProfile       {function}  (user) => void — navigate to another profile
//    onNotificationClick {function}  (timelineId) => void
//    theme               {string}
//    onThemeToggle       {function}
//

const PublicProfile = ({
  userId,
  currentUser,
  onBack,
  onViewTimeline,
  onViewProfile,
  onNotificationClick,
  theme,
  onThemeToggle,
  onAuthRequired,
}) => {

  const [profile,         setProfile]         = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [followLoading,   setFollowLoading]   = useState(false);
  const [error,           setError]           = useState('');
  const [showReportUser,  setShowReportUser]  = useState(false);
  const [reportedUser,    setReportedUser]    = useState(false);
  const [iBlockThem,      setIBlockThem]      = useState(false);
  const [blockLoading,    setBlockLoading]    = useState(false);
  const [showBlockConfirm,setShowBlockConfirm]= useState(false);
  const [relatedRecs,          setRelatedRecs]          = useState([]);
  const [profileCollections,   setProfileCollections]   = useState([]);
  const [profileCollsLoading,  setProfileCollsLoading]  = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    setProfile(null);
    Storage.getPublicProfile(userId)
      .then(data => { setProfile(data); setLoading(false); })
      .catch(err => {
        const msg = (err?.message || '').toLowerCase();
        setError(msg.includes('not found') || msg.includes('404') ? 'Profile not found.' : 'Could not load profile.');
        setLoading(false);
      });
  }, [userId]);

  useEffect(() => {
    if (userId === currentUser?.id) return;
    Storage.checkIsBlocked(userId)
      .then(data => setIBlockThem(data.iBlockThem))
      .catch(() => setIBlockThem(false));
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    setProfileCollsLoading(true);
    Storage.getCollections(userId)
      .then(data => setProfileCollections(data.collections ?? []))
      .catch(() => {})
      .finally(() => setProfileCollsLoading(false));
  }, [userId]);

  // Load recommendations for the first public timeline once the profile is available
  useEffect(() => {
    if (!profile?.timelines?.length) return;
    const firstId = profile.timelines[0].id;
    Storage.getRecommendations(firstId, 6)
      .then(({ recommendations }) => setRelatedRecs(recommendations || []))
      .catch(() => {});
  }, [profile?.user?.id]);

  const handleBlock = async () => {
    if (!currentUser) { onAuthRequired?.('follow'); return; }
    setBlockLoading(true);
    setShowBlockConfirm(false);
    try {
      await Storage.blockUser(userId);
      setIBlockThem(true);
      setProfile(p => p ? { ...p, isFollowing: false, followerCount: Math.max(0, p.followerCount - 1) } : p);
    } catch {}
    setBlockLoading(false);
  };

  const handleUnblock = async () => {
    setBlockLoading(true);
    try {
      await Storage.unblockUser(userId);
      setIBlockThem(false);
    } catch {}
    setBlockLoading(false);
  };

  const handleFollow = async () => {
    if (!currentUser) { onAuthRequired?.('follow'); return; }
    if (!profile || followLoading) return;
    setFollowLoading(true);
    try {
      if (profile.isFollowing) {
        await Storage.unfollow(userId);
        setProfile(p => ({ ...p, isFollowing: false, followerCount: p.followerCount - 1 }));
      } else {
        await Storage.follow(userId);
        setProfile(p => ({ ...p, isFollowing: true, followerCount: p.followerCount + 1 }));
      }
    } catch {
      // silently ignore — state stays consistent
    } finally {
      setFollowLoading(false);
    }
  };

  const isOwnProfile = userId === currentUser?.id;

  // ── Avatar renderer ────────────────────────────────────────────────────────

  const renderAvatar = (u, size) => {
    if (u?.avatar) {
      return (
        <img src={u.avatar} alt=""
          style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
      );
    }
    const initials = ((u?.name) || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: 'linear-gradient(135deg, var(--accent), var(--accent3))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.floor(size * 0.38), fontWeight: 700, color: 'var(--on-accent)',
        userSelect: 'none', flexShrink: 0,
      }}>
        {initials}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ── Nav ── */}
      <nav style={{
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 64,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <button
          onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', flex: 1 }}
          aria-label="Go to feed"
        >
          <div style={{ fontSize: 22, marginRight: 4, filter: 'brightness(0) invert(1)' }} aria-hidden="true">⏱</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--accent)', margin: 0 }}>
            Chronos <span style={{ fontSize: 14, color: 'var(--text3)', fontFamily: 'var(--font-body)', fontWeight: 400 }}>/ Profile</span>
          </h1>
        </button>
        <button
          className="btn-ghost btn-sm"
          onClick={onThemeToggle}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, padding: 0 }}
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
        </button>
        <NotificationBell userId={currentUser.id} onViewTimeline={onNotificationClick} onViewProfile={onViewProfile} />
        <button
          className="btn-ghost btn-sm"
          onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <Icon name="back" size={14} /> Back
        </button>
      </nav>

      <main className="page-main" style={{ maxWidth: 720, margin: '0 auto', padding: '36px 16px 60px' }}>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text3)', fontSize: 14 }}>
            Loading profile…
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <p style={{ color: 'var(--text3)', fontSize: 15, marginBottom: 20 }}>{error}</p>
            <button className="btn-ghost btn-sm" onClick={onBack}>Go back</button>
          </div>
        ) : profile ? (
          <>
            {/* ── Profile header card ── */}
            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-xl)', padding: '28px 32px', marginBottom: 28,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>

                {/* Avatar */}
                {renderAvatar(profile.user, 80)}

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={{
                    fontFamily: 'var(--font-display)', fontSize: 24,
                    color: 'var(--text)', marginBottom: 4,
                  }}>
                    {profile.user.name}
                  </h2>
                  {profile.user.bio && (
                    <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 12 }}>
                      {profile.user.bio}
                    </p>
                  )}

                  {/* Stats row */}
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>
                        {profile.timelines.length}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Timelines</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>
                        {profile.followerCount}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Followers</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>
                        {profile.followingCount}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Following</p>
                    </div>
                  </div>

                  {/* Member since */}
                  <p style={{ fontSize: 12, color: 'var(--text3)' }}>
                    Member since {new Date(profile.user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                  </p>
                </div>

                {/* Follow + Report buttons — only if not own profile */}
                {!isOwnProfile && (
                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                    {profile.isFollowing ? (
                      <button
                        onClick={handleFollow}
                        disabled={followLoading}
                        className="btn-ghost"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 7,
                          opacity: followLoading ? 0.7 : 1,
                          minWidth: 110,
                        }}
                      >
                        <Icon name="userCheck" size={15} />
                        {followLoading ? 'Saving…' : 'Following'}
                      </button>
                    ) : (
                      <button
                        onClick={handleFollow}
                        disabled={followLoading}
                        className="btn-primary"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 7,
                          opacity: followLoading ? 0.7 : 1,
                          minWidth: 110,
                        }}
                      >
                        <Icon name="userPlus" size={15} />
                        {followLoading ? 'Saving…' : 'Follow'}
                      </button>
                    )}
                    {currentUser && !reportedUser && (
                      <button
                        onClick={() => setShowReportUser(true)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--text3)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
                          padding: '2px 4px', borderRadius: 4, transition: 'color 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                      >
                        <Icon name="flag" size={11} /> Report
                      </button>
                    )}
                    {reportedUser && (
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>Reported</span>
                    )}

                    {/* Block / Unblock */}
                    {iBlockThem ? (
                      <button
                        onClick={handleUnblock}
                        disabled={blockLoading}
                        style={{
                          background: 'none', border: 'none', cursor: blockLoading ? 'default' : 'pointer',
                          color: 'var(--text3)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
                          padding: '2px 4px', borderRadius: 4, transition: 'color 0.15s',
                          opacity: blockLoading ? 0.5 : 1,
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                      >
                        <Icon name="ban" size={11} /> {blockLoading ? 'Unblocking…' : 'Unblock'}
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowBlockConfirm(true)}
                        disabled={blockLoading}
                        style={{
                          background: 'none', border: 'none', cursor: blockLoading ? 'default' : 'pointer',
                          color: 'var(--text3)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
                          padding: '2px 4px', borderRadius: 4, transition: 'color 0.15s',
                          opacity: blockLoading ? 0.5 : 1,
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                      >
                        <Icon name="ban" size={11} /> Block
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Public timelines grid ── */}
            <h3 style={{
              fontFamily: 'var(--font-display)', fontSize: 17,
              color: 'var(--text)', marginBottom: 16,
            }}>
              Public Timelines
              {profile.timelines.length > 0 && (
                <span style={{ fontSize: 13, color: 'var(--text3)', fontFamily: 'var(--font-body)', fontWeight: 400, marginLeft: 8 }}>
                  ({profile.timelines.length})
                </span>
              )}
            </h3>

            {profile.timelines.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>
                <div style={{ fontSize: 40, marginBottom: 14 }} aria-hidden="true">🗂</div>
                <p style={{ fontSize: 15 }}>No public timelines yet.</p>
              </div>
            ) : (
              <div className="profile-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {profile.timelines.map(tl => (
                  <ProfileTimelineCard
                    key={tl.id}
                    tl={tl}
                    author={profile.user}
                    onView={() => onViewTimeline(tl, profile.user)}
                    currentUser={currentUser}
                    onAuthRequired={onAuthRequired}
                  />
                ))}
              </div>
            )}
            {/* ── Readers also enjoyed ── */}
            {relatedRecs.length > 0 && (
              <div style={{ marginTop: 40, borderTop: '1px solid var(--border)', paddingTop: 32 }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, marginBottom: 20 }}>
                  Readers also enjoyed
                </h3>
                <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'thin' }}>
                  {relatedRecs.map(({ tl: rec, author: recAuthor }) => {
                    const recAccent = rec.colors?.primary || rec.colors?.accent || 'var(--accent)';
                    return (
                      <button
                        key={rec.id}
                        type="button"
                        onClick={() => onViewTimeline(rec, recAuthor)}
                        style={{
                          flexShrink: 0, width: 200, textAlign: 'left',
                          background: 'var(--surface)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-xl)', overflow: 'hidden',
                          cursor: 'pointer', transition: 'border-color 0.15s, transform 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = recAccent; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
                        aria-label={`View timeline: ${rec.title} by ${recAuthor.name}`}
                      >
                        <div style={{ height: 56, background: rec.bgImage ? `url(${rec.bgImage}) center/cover no-repeat` : recAccent }} />
                        <div style={{ padding: '10px 14px' }}>
                          <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', lineHeight: 1.35, marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {rec.title}
                          </p>
                          <p style={{ fontSize: 11, color: 'var(--text3)' }}>by {recAuthor.name}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          {/* ── Collections ── */}
          {(profileCollsLoading || profileCollections.length > 0) && (
            <div style={{ marginTop: 40, borderTop: '1px solid var(--border)', paddingTop: 32 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, marginBottom: 16 }}>
                Collections
              </h3>
              {profileCollsLoading ? (
                <p style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                  {profileCollections.map(coll => (
                    <a
                      key={coll.id}
                      href={`/collections/${coll.id}`}
                      style={{
                        display: 'block', textDecoration: 'none',
                        background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-xl)', padding: '14px 16px',
                        transition: 'border-color 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                    >
                      <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--accent)', marginBottom: 4 }}>{coll.title}</p>
                      {coll.description && (
                        <p style={{ fontSize: 12, color: 'var(--text2)', margin: '0 0 6px', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{coll.description}</p>
                      )}
                      <p style={{ fontSize: 11, color: 'var(--text3)', margin: 0 }}>
                        {coll.timelineCount} timeline{coll.timelineCount !== 1 ? 's' : ''}
                      </p>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
          </>
        ) : null}
      </main>

      <ReportModal
        open={showReportUser}
        onClose={() => setShowReportUser(false)}
        contentType="user"
        contentId={userId}
        onReported={() => { setReportedUser(true); setShowReportUser(false); }}
      />

      <Modal open={showBlockConfirm} onClose={() => setShowBlockConfirm(false)} title="Block User" width={400}>
        <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 8, lineHeight: 1.6 }}>
          Are you sure you want to block <strong style={{ color: 'var(--text)' }}>{profile?.user?.name}</strong>?
        </p>
        <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
          They will not be notified. You can unblock them at any time from your profile settings.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => setShowBlockConfirm(false)}>Cancel</button>
          <button className="btn-danger" onClick={handleBlock}>Block</button>
        </div>
      </Modal>
    </div>
  );
};

// ── Single timeline card for the profile grid ──────────────────────────────────

const ProfileTimelineCard = ({ tl, author, onView, currentUser, onAuthRequired }) => {
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const sorted    = [...tl.events].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
  const minDate   = sorted.length ? sorted[0].startDate : null;
  const maxDate   = sorted.length ? sorted[sorted.length - 1].endDate : null;
  const minShowEra = sorted.length ? sorted[0].startEra !== 'NONE' : false;
  const maxShowEra = sorted.length ? sorted[sorted.length - 1].endEra !== 'NONE' : false;

  const cat = TIMELINE_CATEGORIES.find(c => c.value === tl.category);

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', overflow: 'hidden',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      display: 'flex', flexDirection: 'column',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)60'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Background image preview — falls back to color strip */}
      {getTimelinePreviewImage(tl) ? (
        <div style={{ height: 130, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
          <img
            src={getTimelinePreviewImage(tl)}
            alt=""
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 60,
            background: 'linear-gradient(to bottom, transparent, var(--surface))' }} aria-hidden="true" />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg, ${tl.colors?.primary || 'var(--accent)'}, ${tl.colors?.secondary || 'var(--accent3)'})` }} aria-hidden="true" />
        </div>
      ) : (
        <div style={{ height: 5, background: `linear-gradient(90deg, ${tl.colors?.primary || 'var(--accent)'}, ${tl.colors?.secondary || 'var(--accent3)'})` }} aria-hidden="true" />
      )}

      <div style={{ padding: '14px 16px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Title */}
        <h4
          onClick={onView}
          style={{
            fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--text)',
            marginBottom: 6, cursor: 'pointer', lineHeight: 1.35, transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text)'}
        >
          {tl.title}
        </h4>

        {/* Category */}
        {cat && (
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 11, background: 'var(--bg3)', borderRadius: 5, padding: '2px 8px', color: 'var(--text2)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span aria-hidden="true">{cat.emoji}</span> {cat.label}
            </span>
          </div>
        )}

        {/* Description preview */}
        {hasTextContent(tl.description) && (
          <p style={{
            fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 8,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {stripHtml(tl.description)}
          </p>
        )}

        {/* Date range */}
        {minDate && (
          <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
            {formatDate(minDate, minShowEra)} → {formatDate(maxDate, maxShowEra)}
          </p>
        )}

        <div style={{ flex: 1 }} />

        {/* Footer: stats + view button */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon name="heart" size={12} color="var(--text3)" /> {tl.likeCount || 0}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon name="message" size={12} color="var(--text3)" /> {tl.commentCount || 0}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              {tl.events.length} event{tl.events.length !== 1 ? 's' : ''}
              {tl.orientation === 'storyline' && Array.isArray(tl.chapters) && tl.chapters.length > 0 && (
                <> · {tl.chapters.length} chapter{tl.chapters.length !== 1 ? 's' : ''}</>
              )}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {currentUser && (
              <button
                onClick={() => setShowCollectionModal(true)}
                aria-label="Add to collection"
                title="Add to collection"
                className="btn-ghost btn-sm"
                style={{ fontSize: 12, padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Icon name="layers" size={13} />
              </button>
            )}
            <button
              onClick={onView}
              className="btn-ghost btn-sm"
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              View
            </button>
          </div>
        </div>
      </div>

      <AddToCollectionModal
        open={showCollectionModal}
        onClose={() => setShowCollectionModal(false)}
        timelineId={tl.id}
        timelineName={tl.title}
        currentUser={currentUser}
      />
    </div>
  );
};

export default PublicProfile;
